/**
 * oRPC contract definitions for admin, adminV2, and system routes.
 *
 * Every procedure declares its HTTP method, path, input schema, and an
 * output schema. Some legacy routes still use generic outputs, but we
 * tighten them incrementally as shared server/dashboard contracts become
 * explicit.
 */
import { oc } from "@orpc/contract";
import { z } from "zod";

export * from "./schemas";

import {
  AdminLoginInputSchema,
  AdminRefreshInputSchema,
  AdminUuidParamSchema,
  AdminIdParamSchema,
  AdminRejectTopUpInputSchema,
  AdminApproveRedeemBankInputSchema,
  AdminRejectRedeemInputSchema,
  AdminSubmitFreezeInputSchema,
  AdminMintRequestInputSchema,
  AdminAddMerchantInputSchema,
  AdminEditMerchantInputSchema,
  AdminAddCurrencyInputSchema,
  AdminEditCurrencyInputSchema,
  AdminToggleCurrencyFlagInputSchema,
  AdminChangeCkBtcStatusInputSchema,
  AdminHideVaultInputSchema,
  AdminResendFireblocksWebhookByIDInputSchema,
  AdminSettlementSummarizeInputSchema,
  AdminUpdateSettlementCTFInputSchema,
  AdminUpdateSettlementFTCInputSchema,
  AdminResendSettlementWebhooksInputSchema,
  AdminPaymentLinkDetailInputSchema,
  AdminReturnDepositInputSchema,
  AdminGetDPBalanceInputSchema,
  AdminTransferKycInputSchema,
  AdminAddBlacklistedDestinationInputSchema,
  AdminDeleteBlacklistedDestinationInputSchema,
  AdminTransferP2PDetailInputSchema,
  AdminAddCaptchaRequirementInputSchema,
  AdminUpdateCaptchaRequirementInputSchema,
  AdminDetailCaptchaRequirementInputSchema,
  AdminCreateAnnouncementInputSchema,
  AdminUpdateAnnouncementInputSchema,
  AdminPermissionTemplateInputSchema,
  AdminUpdatePermissionTemplateInputSchema,
  AdminPublicAddressParamSchema,
  AdminUserRestrictionsInputSchema,
  AdminAssignPermissionListInputSchema,
  AdminAddEWalletInputSchema,
  AdminDeleteEWalletInputSchema,
  AdminEscrowUuidInputSchema,
  AdminPpobProductIdInputSchema,
  AdminPpobTransactionUuidInputSchema,
  AdminPpobProductUpdateInputSchema,
  AdminListQuerySchema,
  AdminGenericResponseSchema,
  AdminActionResponseSchema,
  AdminNoContentResponseSchema,
  AdminFreezeListResponseSchema,
  AdminFreezeDetailSchema,
  AdminMintListResponseSchema,
  AdminMintAddressSuggestionListResponseSchema,
  AdminMintDetailSchema,
  AdminPermissionTemplateListResponseSchema,
  AdminPermissionTemplateMutationResponseSchema,
  AdminUserRestrictionsResponseSchema,
  AdminAssignPermissionListResponseSchema,
  AdminDeleteUserRestrictionsResponseSchema,
  AdminAnnouncementListResponseSchema,
  AdminAnnouncementMutationResponseSchema,
  AdminCaptchaRequirementListResponseSchema,
  AdminCaptchaRequirementDetailResponseSchema,
  AdminCaptchaRequirementMutationResponseSchema,
  AdminEwalletAccountListResponseSchema,
  AdminBankingAccountListResponseSchema,
  AdminSpreadListResponseSchema,
  AdminUserLimitListResponseSchema,
  AdminUserLimitSchema,
  AdminReferralProgramListResponseSchema,
  AdminReferralProgramDetailResponseSchema,
  AdminReferralCodeListResponseSchema,
  AdminReferralCodeDetailResponseSchema,
  AdminSystemStatusListResponseSchema,
  AdminSystemStatusSchema,
  AdminMerchantListResponseSchema,
  AdminMerchantSchema,
  AdminCurrencyListResponseSchema,
  AdminCurrencySchema,
  AdminTonPrefunderListResponseSchema,
  AdminBlacklistedDestinationListResponseSchema,
  AdminBlacklistedDestinationSchema,
  AdminKycUserListResponseSchema,
  AdminSpamTopupListResponseSchema,
  AdminSpamTopupDeleteResponseSchema,
  AdminV2RedeemUuidInputSchema,
  AdminV2RedeemRejectInputSchema,
  AdminV2SpreadAddInputSchema,
  AdminV2SpreadEditInputSchema,
  AdminV2SpreadBulkEditInputSchema,
  AdminV2UserLimitAddInputSchema,
  AdminV2UserLimitEditInputSchema,
  AdminV2UserLimitIdInputSchema,
  AdminV2FireblocksResendByIdInputSchema,
  AdminV2FireblocksRefreshVaultAssetInputSchema,
  AdminV2FireblocksRefreshVaultAssetResponseSchema,
  AdminV2FireblocksRequeueValidationInputSchema,
  AdminV2FireblocksRequeueValidationResponseSchema,
  AdminV2EventReferralProgramCreateInputSchema,
  AdminV2EventReferralProgramIdInputSchema,
  AdminV2EventReferralProgramUpdateInputSchema,
  AdminV2EventReferralCodeCreateInputSchema,
  AdminV2EventReferralCodeIdInputSchema,
  AdminV2EventReferralCodeUpdateInputSchema,
  AdminV2BankingAddInputSchema,
  AdminV2BankingDeleteInputSchema,
  AdminV2SpamTopupDeleteInputSchema,
  SystemUpdateStatusInputSchema,
  // Wallet V1 schemas
  WalletAuthInputSchema,
  WalletRegisterInputSchema,
  WalletTransactionListInputSchema,
  WalletTransactionDetailInputSchema,
  WalletExchangeRateInputSchema,
  WalletExchangeRateResponseSchema,
  WalletRequestDepositAddressInputSchema,
  WalletRequestDepositAddressResponseSchema,
  WalletBankingAddInputSchema,
  WalletBankingUuidInputSchema,
  WalletBankingEditInputSchema,
  BankAccountResponseSchema,
  BankAccountListResponseSchema,
  WalletP2PAddressInputSchema,
  WalletP2PAddressUuidInputSchema,
  WalletP2PAddressEditInputSchema,
  WalletMerchantAddInputSchema,
  WalletMerchantEditInputSchema,
  WalletMerchantResponseSchema,
  WalletMerchantUploadImageResponseSchema,
  WalletPaymentLinkCreateInputSchema,
  WalletPaymentLinkDetailInputSchema,
  WalletPaymentLinkVoidInputSchema,
  WalletEscrowSubmitDepositInputSchema,
  WalletPpobPrepaidOrderInputSchema,
  WalletPpobPostpaidOrderInputSchema,
  WalletPpobPostpaidInquiryInputSchema,
  WalletPpobProductListInputSchema,
  WalletPpobRecentDestinationsInputSchema,
  WalletPpobRecentDestinationsResponseSchema,
  WalletPpobMobileProviderInputSchema,
  WalletPpobPlnInquiryInputSchema,
  WalletPpobPlnInquiryResponseSchema,
  WalletPpobOrderEscrowResponseSchema,
  WalletMigrationStatusResponseSchema,
  WalletMigrationCreateUnsignedJsResponseSchema,
  WalletMigrationReturnJsMintScResponseSchema,
  GenericResponseSchema,
  // Wallet V2 schemas
  WalletV2RedeemInitiateInputSchema,
  WalletV2RedeemBankSendInputSchema,
  WalletV2RedeemEwalletSendInputSchema,
  WalletV2RedeemInitiateResponseSchema,
  WalletV2RedeemSendResponseSchema,
  WalletV2ActivityListInputSchema,
  WalletV2ActivityDetailInputSchema,
  WalletV2ActivityListResponseSchema,
  WalletV2EwalletAddInputSchema,
  WalletV2EwalletUuidInputSchema,
  WalletV2EwalletEditInputSchema,
  WalletV2EwalletFavoriteInputSchema,
  WalletV2BankingAddInputSchema,
  WalletV2BankingUuidInputSchema,
  WalletV2BankingEditInputSchema,
  WalletV2BankingFavoriteInputSchema,
  WalletV2ReferralInputSchema,
  WalletV2ReferralSubmitResponseSchema,
  WalletV2ReferralDetailsResponseSchema,
  WalletV2ReferralJourneyResponseSchema,
  WalletV2BankTransferFeeResponseSchema,
  WalletV2PayBankTransferInitiateInputSchema,
  WalletV2PayBankTransferInitiateResponseSchema,
  WalletV2PayBankTransferSendInputSchema,
  WalletV2PayBankTransferSendResponseSchema,
  WalletV2TransferInitiateInputSchema,
  WalletV2TransferSendInputSchema,
  WalletV2TransferInitiateResponseSchema,
  WalletV2TransferSendResponseSchema,
  WalletV2KycAccessTokenInputSchema,
  WalletV2KycAccessTokenResponseSchema,
  WalletV2KycDetailResponseSchema,
  WalletV2KycBenefitsResponseSchema,
  WalletV2VirtualAccountInputSchema,
  WalletV2VirtualAccountSupportedResponseSchema,
  WalletV2VirtualAccountTopUpInstructionResponseSchema,
  WalletV2VirtualAccountCreateResponseSchema,
  SupportedBankListResponseSchema,
  SupportedEwalletListResponseSchema,
  EwalletAccountResponseSchema,
  EwalletAccountListResponseSchema,
  NoContentResponseSchema,
  LegacyInvalidStatusResponseSchema,
} from "./schemas";

// ─── Admin V1 ──────────────────────────────────────────────────────

const admin = oc.router({
  // Auth
  login: oc
    .route({ method: "POST", path: "/admin/v1/login-with-otp" })
    .input(AdminLoginInputSchema)
    .output(AdminGenericResponseSchema),
  refresh: oc
    .route({ method: "POST", path: "/admin/v1/refresh" })
    .input(AdminRefreshInputSchema)
    .output(AdminGenericResponseSchema),

  // Top-up
  topup: oc.router({
    retryCrypto: oc
      .route({ method: "POST", path: "/admin/v1/retry-topup-crypto/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
    retryBank: oc
      .route({ method: "POST", path: "/admin/v1/retry-topup-bank/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
    reject: oc
      .route({ method: "POST", path: "/admin/v1/reject-topup" })
      .input(AdminRejectTopUpInputSchema)
      .output(AdminGenericResponseSchema),
    list: oc
      .route({ method: "GET", path: "/admin/v1/topup-list" })
      .input(AdminListQuerySchema)
      .output(AdminGenericResponseSchema),
  }),

  // Redeem
  redeem: oc.router({
    bank: oc.router({
      approve: oc
        .route({ method: "POST", path: "/admin/v1/approve-redeem-bank/{uuid}" })
        .input(AdminApproveRedeemBankInputSchema)
        .output(AdminGenericResponseSchema),
      reject: oc
        .route({ method: "POST", path: "/admin/v1/reject-redeem-bank/{uuid}" })
        .input(AdminRejectRedeemInputSchema)
        .output(AdminGenericResponseSchema),
      list: oc
        .route({ method: "GET", path: "/admin/v1/redeem-bank-list" })
        .input(AdminListQuerySchema)
        .output(AdminGenericResponseSchema),
    }),
    crypto: oc.router({
      retry: oc
        .route({
          method: "POST",
          path: "/admin/v1/retry-redeem-crypto/{uuid}",
        })
        .input(AdminUuidParamSchema)
        .output(AdminGenericResponseSchema),
      reject: oc
        .route({
          method: "POST",
          path: "/admin/v1/reject-redeem-crypto/{uuid}",
        })
        .input(AdminRejectRedeemInputSchema)
        .output(AdminGenericResponseSchema),
      list: oc
        .route({ method: "GET", path: "/admin/v1/redeem-crypto-list" })
        .input(AdminListQuerySchema)
        .output(AdminGenericResponseSchema),
    }),
    retryUnknown: oc
      .route({
        method: "POST",
        path: "/admin/v1/retry-redeem-unknown/{uuid}",
      })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
  }),

  // Transaction
  transaction: oc.router({
    getOne: oc
      .route({ method: "GET", path: "/admin/v1/transaction/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
  }),

  // Freeze
  freeze: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v1/freeze-list" })
      .input(AdminListQuerySchema)
      .output(AdminFreezeListResponseSchema),
    get: oc
      .route({ method: "GET", path: "/admin/v1/freeze/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminFreezeDetailSchema),
    submit: oc
      .route({ method: "POST", path: "/admin/v1/freeze" })
      .input(AdminSubmitFreezeInputSchema)
      .output(AdminGenericResponseSchema),
    approve: oc
      .route({ method: "POST", path: "/admin/v1/approve-freeze/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
    reject: oc
      .route({ method: "POST", path: "/admin/v1/reject-freeze/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
  }),

  // Mint
  mint: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v1/mint-list" })
      .input(AdminListQuerySchema)
      .output(AdminMintListResponseSchema),
    addressSuggestion: oc
      .route({ method: "GET", path: "/admin/v1/mint-address-suggestion" })
      .output(AdminMintAddressSuggestionListResponseSchema),
    get: oc
      .route({ method: "GET", path: "/admin/v1/mint/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminMintDetailSchema),
    request: oc
      .route({ method: "POST", path: "/admin/v1/mint" })
      .input(AdminMintRequestInputSchema)
      .output(AdminGenericResponseSchema),
    approve: oc
      .route({ method: "POST", path: "/admin/v1/approve-mint/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
    reject: oc
      .route({ method: "POST", path: "/admin/v1/reject-mint/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
    updateStatus: oc
      .route({ method: "POST", path: "/admin/v1/update-mint-status/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
  }),

  // Merchant
  merchant: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v1/merchants" })
      .input(AdminListQuerySchema.optional())
      .output(AdminMerchantListResponseSchema),
    get: oc
      .route({ method: "GET", path: "/admin/v1/merchant/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminMerchantSchema),
    add: oc
      .route({ method: "POST", path: "/admin/v1/merchant" })
      .input(AdminAddMerchantInputSchema)
      .output(AdminMerchantSchema),
    edit: oc
      .route({ method: "PUT", path: "/admin/v1/merchant" })
      .input(AdminEditMerchantInputSchema)
      .output(AdminMerchantSchema),
    delete: oc
      .route({ method: "DELETE", path: "/admin/v1/merchant/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminNoContentResponseSchema),
  }),

  // Currency
  currency: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v1/currencies" })
      .input(AdminListQuerySchema.optional())
      .output(AdminCurrencyListResponseSchema),
    get: oc
      .route({ method: "GET", path: "/admin/v1/currency/{id}" })
      .input(AdminIdParamSchema)
      .output(AdminCurrencySchema),
    add: oc
      .route({ method: "POST", path: "/admin/v1/currency" })
      .input(AdminAddCurrencyInputSchema)
      .output(AdminCurrencySchema),
    edit: oc
      .route({ method: "PUT", path: "/admin/v1/currency" })
      .input(AdminEditCurrencyInputSchema)
      .output(AdminCurrencySchema),
    delete: oc
      .route({ method: "DELETE", path: "/admin/v1/currency/{id}" })
      .input(AdminIdParamSchema)
      .output(AdminNoContentResponseSchema),
    toggleFlag: oc
      .route({ method: "POST", path: "/admin/v1/currency/toggle-flag" })
      .input(AdminToggleCurrencyFlagInputSchema)
      .output(AdminNoContentResponseSchema),
  }),

  // ckBTC
  ckbtc: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v1/ckbtc-transactions" })
      .input(AdminListQuerySchema)
      .output(AdminGenericResponseSchema),
    detail: oc
      .route({ method: "GET", path: "/admin/v1/ckbtc-transaction/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
    disburse: oc
      .route({
        method: "POST",
        path: "/admin/v1/ckbtc-transaction/{uuid}/disburse",
      })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
    revalidate: oc
      .route({
        method: "POST",
        path: "/admin/v1/ckbtc-transaction/{uuid}/revalidate",
      })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
    changeStatus: oc
      .route({
        method: "POST",
        path: "/admin/v1/ckbtc-transaction/change-status",
      })
      .input(AdminChangeCkBtcStatusInputSchema)
      .output(AdminGenericResponseSchema),
  }),

  // Fireblocks
  fireblocks: oc.router({
    hideVault: oc
      .route({ method: "POST", path: "/admin/v1/fireblocks/hide-vault" })
      .input(AdminHideVaultInputSchema)
      .output(AdminGenericResponseSchema),
    unhideVault: oc
      .route({ method: "POST", path: "/admin/v1/fireblocks/unhide-vault" })
      .input(AdminHideVaultInputSchema)
      .output(AdminGenericResponseSchema),
    resendWebhooksById: oc
      .route({
        method: "POST",
        path: "/admin/v1/fireblocks/resend-webhooks-by-id",
      })
      .input(AdminResendFireblocksWebhookByIDInputSchema)
      .output(AdminGenericResponseSchema),
    resendWebhooks: oc
      .route({
        method: "POST",
        path: "/admin/v1/fireblocks/resend-webhooks",
      })
      .output(AdminGenericResponseSchema),
  }),

  // Settlement
  settlement: oc.router({
    ctf: oc.router({
      list: oc
        .route({ method: "GET", path: "/admin/v1/settlement/ctf" })
        .input(AdminListQuerySchema)
        .output(AdminGenericResponseSchema),
      detail: oc
        .route({ method: "GET", path: "/admin/v1/settlement/ctf/{uuid}" })
        .input(AdminUuidParamSchema)
        .output(AdminGenericResponseSchema),
      summarize: oc
        .route({
          method: "POST",
          path: "/admin/v1/settlement/ctf/summarize",
        })
        .input(AdminSettlementSummarizeInputSchema)
        .output(AdminGenericResponseSchema),
      bulkUpdate: oc
        .route({
          method: "POST",
          path: "/admin/v1/settlement/ctf/bulk-update",
        })
        .input(AdminUpdateSettlementCTFInputSchema)
        .output(AdminGenericResponseSchema),
    }),
    ftc: oc.router({
      list: oc
        .route({ method: "GET", path: "/admin/v1/settlement/ftc" })
        .input(AdminListQuerySchema)
        .output(AdminGenericResponseSchema),
      detail: oc
        .route({ method: "GET", path: "/admin/v1/settlement/ftc/{uuid}" })
        .input(AdminUuidParamSchema)
        .output(AdminGenericResponseSchema),
      summarize: oc
        .route({
          method: "POST",
          path: "/admin/v1/settlement/ftc/summarize",
        })
        .input(AdminSettlementSummarizeInputSchema)
        .output(AdminGenericResponseSchema),
      bulkUpdate: oc
        .route({
          method: "POST",
          path: "/admin/v1/settlement/ftc/bulk-update",
        })
        .input(AdminUpdateSettlementFTCInputSchema)
        .output(AdminGenericResponseSchema),
    }),
    resendWebhooks: oc
      .route({
        method: "POST",
        path: "/admin/v1/settlement/resend-webhooks",
      })
      .input(AdminResendSettlementWebhooksInputSchema)
      .output(AdminGenericResponseSchema),
  }),

  // Payment Link
  paymentLink: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v1/payment-links" })
      .input(AdminListQuerySchema)
      .output(AdminGenericResponseSchema),
    detail: oc
      .route({ method: "GET", path: "/admin/v1/payment-link/{payment_uuid}" })
      .input(AdminPaymentLinkDetailInputSchema)
      .output(AdminGenericResponseSchema),
    returnDeposit: oc
      .route({
        method: "POST",
        path: "/admin/v1/payment-link/return-deposit",
      })
      .input(AdminReturnDepositInputSchema)
      .output(AdminGenericResponseSchema),
  }),

  // Standalone admin endpoints
  getDPBalance: oc
    .route({ method: "GET", path: "/admin/v1/dp-balance" })
    .input(AdminGetDPBalanceInputSchema)
    .output(AdminGenericResponseSchema),
  transferKyc: oc
    .route({ method: "POST", path: "/admin/v1/transfer-kyc" })
    .input(AdminTransferKycInputSchema)
    .output(AdminGenericResponseSchema),
  retryReferralReward: oc
    .route({
      method: "POST",
      path: "/admin/v1/retry-referral-reward/{uuid}",
    })
    .input(AdminUuidParamSchema)
    .output(AdminGenericResponseSchema),

  // TON
  ton: oc.router({
    prefunder: oc
      .route({ method: "GET", path: "/admin/v1/ton-prefunder" })
      .output(AdminTonPrefunderListResponseSchema),
  }),

  // Blacklisted Destination
  blacklistedDestination: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v1/blacklisted-destinations" })
      .input(AdminListQuerySchema)
      .output(AdminBlacklistedDestinationListResponseSchema),
    detail: oc
      .route({
        method: "GET",
        path: "/admin/v1/blacklisted-destination/{uuid}",
      })
      .input(AdminUuidParamSchema)
      .output(AdminBlacklistedDestinationSchema),
    add: oc
      .route({
        method: "POST",
        path: "/admin/v1/blacklisted-destination",
      })
      .input(AdminAddBlacklistedDestinationInputSchema)
      .output(AdminBlacklistedDestinationSchema),
    delete: oc
      .route({
        method: "DELETE",
        path: "/admin/v1/blacklisted-destination/{uuid}",
      })
      .input(AdminDeleteBlacklistedDestinationInputSchema)
      .output(AdminNoContentResponseSchema),
  }),

  // Transfer P2P
  transferP2P: oc.router({
    crossNetworkList: oc
      .route({
        method: "GET",
        path: "/admin/v1/transfer-p2p/cross-network",
      })
      .input(AdminListQuerySchema)
      .output(AdminGenericResponseSchema),
    detail: oc
      .route({ method: "GET", path: "/admin/v1/transfer-p2p/{uuid}" })
      .input(AdminTransferP2PDetailInputSchema)
      .output(AdminGenericResponseSchema),
    retryMinting: oc
      .route({
        method: "POST",
        path: "/admin/v1/transfer-p2p/{uuid}/retry-minting",
      })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
  }),

  // Captcha
  captcha: oc.router({
    add: oc
      .route({ method: "POST", path: "/admin/v1/captcha-requirement" })
      .input(AdminAddCaptchaRequirementInputSchema)
      .output(AdminCaptchaRequirementMutationResponseSchema),
    update: oc
      .route({ method: "PUT", path: "/admin/v1/captcha-requirement" })
      .input(AdminUpdateCaptchaRequirementInputSchema)
      .output(AdminCaptchaRequirementMutationResponseSchema),
    list: oc
      .route({ method: "GET", path: "/admin/v1/captcha-requirements" })
      .input(AdminListQuerySchema)
      .output(AdminCaptchaRequirementListResponseSchema),
    detail: oc
      .route({
        method: "GET",
        path: "/admin/v1/captcha-requirement/{id}",
      })
      .input(AdminDetailCaptchaRequirementInputSchema)
      .output(AdminCaptchaRequirementDetailResponseSchema),
  }),

  // Announcement
  announcement: oc.router({
    create: oc
      .route({ method: "POST", path: "/admin/v1/announcement" })
      .input(AdminCreateAnnouncementInputSchema)
      .output(AdminAnnouncementMutationResponseSchema),
    list: oc
      .route({ method: "GET", path: "/admin/v1/announcements" })
      .input(AdminListQuerySchema.optional())
      .output(AdminAnnouncementListResponseSchema),
    update: oc
      .route({ method: "PUT", path: "/admin/v1/announcement" })
      .input(AdminUpdateAnnouncementInputSchema)
      .output(AdminAnnouncementMutationResponseSchema),
    delete: oc
      .route({ method: "DELETE", path: "/admin/v1/announcement/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminActionResponseSchema),
  }),

  // Permission
  permission: oc.router({
    templateList: oc
      .route({ method: "GET", path: "/admin/v1/permission-templates" })
      .input(AdminListQuerySchema.optional())
      .output(AdminPermissionTemplateListResponseSchema),
    templateCreate: oc
      .route({ method: "POST", path: "/admin/v1/permission-template" })
      .input(AdminPermissionTemplateInputSchema)
      .output(AdminPermissionTemplateMutationResponseSchema),
    templateUpdate: oc
      .route({ method: "PUT", path: "/admin/v1/permission-template/{uuid}" })
      .input(AdminUpdatePermissionTemplateInputSchema)
      .output(AdminPermissionTemplateMutationResponseSchema),
    templateDelete: oc
      .route({
        method: "DELETE",
        path: "/admin/v1/permission-template/{uuid}",
      })
      .input(AdminUuidParamSchema)
      .output(AdminActionResponseSchema),
  }),

  // User Restrictions
  userRestrictions: oc.router({
    get: oc
      .route({
        method: "GET",
        path: "/admin/v1/user-restrictions/{public_address}",
      })
      .input(AdminPublicAddressParamSchema)
      .output(AdminUserRestrictionsResponseSchema),
    update: oc
      .route({ method: "PUT", path: "/admin/v1/user-restrictions" })
      .input(AdminUserRestrictionsInputSchema)
      .output(AdminGenericResponseSchema),
    delete: oc
      .route({
        method: "DELETE",
        path: "/admin/v1/user-restrictions/{public_address}",
      })
      .input(AdminPublicAddressParamSchema)
      .output(AdminDeleteUserRestrictionsResponseSchema),
    assignPermissions: oc
      .route({
        method: "POST",
        path: "/admin/v1/user-permissions/assign",
      })
      .input(AdminAssignPermissionListInputSchema)
      .output(AdminAssignPermissionListResponseSchema),
  }),

  // E-wallet
  ewallet: oc.router({
    list: oc
      .route({
        method: "GET",
        path: "/admin/v1/ewallets",
      })
      .input(AdminListQuerySchema)
      .output(AdminEwalletAccountListResponseSchema),
    add: oc
      .route({ method: "POST", path: "/admin/v1/ewallets/{publicAddress}" })
      .input(AdminAddEWalletInputSchema)
      .output(AdminActionResponseSchema),
    delete: oc
      .route({
        method: "DELETE",
        path: "/admin/v1/ewallets/{publicAddress}/{ewalletUuid}",
      })
      .input(AdminDeleteEWalletInputSchema)
      .output(AdminNoContentResponseSchema),
  }),

  // Escrow
  escrow: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v1/escrow-requests" })
      .input(AdminListQuerySchema)
      .output(AdminGenericResponseSchema),
    detail: oc
      .route({ method: "GET", path: "/admin/v1/escrow/{uuid}" })
      .input(AdminEscrowUuidInputSchema)
      .output(AdminGenericResponseSchema),
    reject: oc
      .route({ method: "POST", path: "/admin/v1/escrow/{uuid}/reject" })
      .input(AdminEscrowUuidInputSchema)
      .output(AdminGenericResponseSchema),
    dispatch: oc
      .route({ method: "POST", path: "/admin/v1/escrow/{uuid}/dispatch" })
      .input(AdminEscrowUuidInputSchema)
      .output(AdminGenericResponseSchema),
    manualBurn: oc
      .route({
        method: "POST",
        path: "/admin/v1/escrow/{uuid}/manual-burn",
      })
      .input(AdminEscrowUuidInputSchema)
      .output(AdminGenericResponseSchema),
  }),

  // PPOB
  ppob: oc.router({
    transactionList: oc
      .route({ method: "GET", path: "/admin/v1/ppob/transactions" })
      .input(AdminListQuerySchema)
      .output(AdminGenericResponseSchema),
    transactionDetail: oc
      .route({ method: "GET", path: "/admin/v1/ppob/transaction/{uuid}" })
      .input(AdminPpobTransactionUuidInputSchema)
      .output(AdminGenericResponseSchema),
    syncPricelist: oc
      .route({ method: "POST", path: "/admin/v1/ppob/sync-pricelist" })
      .output(AdminGenericResponseSchema),
    productList: oc
      .route({ method: "GET", path: "/admin/v1/ppob/products" })
      .input(AdminListQuerySchema)
      .output(AdminGenericResponseSchema),
    productDetail: oc
      .route({ method: "GET", path: "/admin/v1/ppob/product/{id}" })
      .input(AdminPpobProductIdInputSchema)
      .output(AdminGenericResponseSchema),
    productUpdate: oc
      .route({ method: "PUT", path: "/admin/v1/ppob/product/{id}" })
      .input(AdminPpobProductUpdateInputSchema)
      .output(AdminGenericResponseSchema),
  }),

  // KYC
  kyc: oc.router({
    userList: oc
      .route({ method: "GET", path: "/admin/v1/kyc/users" })
      .input(AdminListQuerySchema)
      .output(AdminKycUserListResponseSchema),
  }),
});

// ─── Admin V2 ──────────────────────────────────────────────────────

const adminV2 = oc.router({
  // Redeem
  redeem: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v2/redeem-list" })
      .input(AdminListQuerySchema)
      .output(AdminGenericResponseSchema),
    detail: oc
      .route({ method: "GET", path: "/admin/v2/redeem/{uuid}" })
      .input(AdminV2RedeemUuidInputSchema)
      .output(AdminGenericResponseSchema),
    approve: oc
      .route({ method: "POST", path: "/admin/v2/redeem/{uuid}/approve" })
      .input(AdminV2RedeemUuidInputSchema)
      .output(AdminGenericResponseSchema),
    reject: oc
      .route({ method: "POST", path: "/admin/v2/redeem/{uuid}/reject" })
      .input(AdminV2RedeemRejectInputSchema)
      .output(AdminGenericResponseSchema),
  }),

  // Bank Top-up
  bankTopup: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v2/bank-topup-list" })
      .input(AdminListQuerySchema)
      .output(AdminGenericResponseSchema),
    detail: oc
      .route({ method: "GET", path: "/admin/v2/bank-topup/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminGenericResponseSchema),
  }),

  // Fireblocks
  fireblocks: oc.router({
    resendById: oc
      .route({ method: "POST", path: "/admin/v2/fireblocks/resend-by-id" })
      .input(AdminV2FireblocksResendByIdInputSchema)
      .output(AdminGenericResponseSchema),
    resendFailed: oc
      .route({
        method: "POST",
        path: "/admin/v2/fireblocks/resend-failed",
      })
      .output(AdminGenericResponseSchema),
    refreshVaultAssetBalance: oc
      .route({
        method: "POST",
        path: "/admin/v2/fireblocks/refresh-vault-asset-balance",
      })
      .input(AdminV2FireblocksRefreshVaultAssetInputSchema)
      .output(AdminV2FireblocksRefreshVaultAssetResponseSchema),
    requeueValidation: oc
      .route({
        method: "POST",
        path: "/admin/v2/fireblocks-validation/requeue",
      })
      .input(AdminV2FireblocksRequeueValidationInputSchema)
      .output(AdminV2FireblocksRequeueValidationResponseSchema),
  }),

  // Spread
  spread: oc.router({
    add: oc
      .route({ method: "POST", path: "/admin/v2/spread" })
      .input(AdminV2SpreadAddInputSchema)
      .output(AdminActionResponseSchema),
    list: oc
      .route({ method: "GET", path: "/admin/v2/spread" })
      .input(AdminListQuerySchema.optional())
      .output(AdminSpreadListResponseSchema),
    edit: oc
      .route({ method: "PUT", path: "/admin/v2/spread/{uuid}" })
      .input(AdminV2SpreadEditInputSchema)
      .output(AdminActionResponseSchema),
    delete: oc
      .route({ method: "DELETE", path: "/admin/v2/spread/{uuid}" })
      .input(AdminUuidParamSchema)
      .output(AdminActionResponseSchema),
    bulkEdit: oc
      .route({ method: "POST", path: "/admin/v2/spread/bulk-edit" })
      .input(AdminV2SpreadBulkEditInputSchema)
      .output(AdminActionResponseSchema),
  }),

  // User Limit
  userLimit: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v2/user-limits" })
      .input(AdminListQuerySchema)
      .output(AdminUserLimitListResponseSchema),
    add: oc
      .route({ method: "POST", path: "/admin/v2/user-limit" })
      .input(AdminV2UserLimitAddInputSchema)
      .output(AdminActionResponseSchema),
    detail: oc
      .route({ method: "GET", path: "/admin/v2/user-limit/{id}" })
      .input(AdminV2UserLimitIdInputSchema)
      .output(AdminUserLimitSchema),
    edit: oc
      .route({ method: "PUT", path: "/admin/v2/user-limit/{id}" })
      .input(AdminV2UserLimitEditInputSchema)
      .output(AdminActionResponseSchema),
    delete: oc
      .route({ method: "DELETE", path: "/admin/v2/user-limit/{id}" })
      .input(AdminV2UserLimitIdInputSchema)
      .output(AdminNoContentResponseSchema),
  }),

  // Event Referral
  eventReferral: oc.router({
    programs: oc.router({
      create: oc
        .route({
          method: "POST",
          path: "/admin/v2/event-referral/programs",
        })
        .input(AdminV2EventReferralProgramCreateInputSchema)
        .output(AdminActionResponseSchema),
      list: oc
        .route({
          method: "GET",
          path: "/admin/v2/event-referral/programs",
        })
        .input(AdminListQuerySchema)
        .output(AdminReferralProgramListResponseSchema),
      detail: oc
        .route({
          method: "GET",
          path: "/admin/v2/event-referral/programs/{id}",
        })
        .input(AdminV2EventReferralProgramIdInputSchema)
        .output(AdminReferralProgramDetailResponseSchema),
      update: oc
        .route({
          method: "PUT",
          path: "/admin/v2/event-referral/programs/{id}",
        })
        .input(AdminV2EventReferralProgramUpdateInputSchema)
        .output(AdminActionResponseSchema),
      delete: oc
        .route({
          method: "DELETE",
          path: "/admin/v2/event-referral/programs/{id}",
        })
        .input(AdminV2EventReferralProgramIdInputSchema)
        .output(AdminActionResponseSchema),
    }),
    codes: oc.router({
      create: oc
        .route({
          method: "POST",
          path: "/admin/v2/event-referral/codes",
        })
        .input(AdminV2EventReferralCodeCreateInputSchema)
        .output(AdminActionResponseSchema),
      list: oc
        .route({
          method: "GET",
          path: "/admin/v2/event-referral/codes",
        })
        .input(AdminListQuerySchema)
        .output(AdminReferralCodeListResponseSchema),
      detail: oc
        .route({
          method: "GET",
          path: "/admin/v2/event-referral/codes/{id}",
        })
        .input(AdminV2EventReferralCodeIdInputSchema)
        .output(AdminReferralCodeDetailResponseSchema),
      update: oc
        .route({
          method: "PUT",
          path: "/admin/v2/event-referral/codes/{id}",
        })
        .input(AdminV2EventReferralCodeUpdateInputSchema)
        .output(AdminActionResponseSchema),
      delete: oc
        .route({
          method: "DELETE",
          path: "/admin/v2/event-referral/codes/{id}",
        })
        .input(AdminV2EventReferralCodeIdInputSchema)
        .output(AdminActionResponseSchema),
    }),
  }),

  // Banking
  banking: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v2/banks" })
      .input(AdminListQuerySchema)
      .output(AdminBankingAccountListResponseSchema),
    add: oc
      .route({ method: "POST", path: "/admin/v2/banks/{public_address}" })
      .input(AdminV2BankingAddInputSchema)
      .output(AdminActionResponseSchema),
    delete: oc
      .route({
        method: "DELETE",
        path: "/admin/v2/banks/{public_address}/{bank_uuid}",
      })
      .input(AdminV2BankingDeleteInputSchema)
      .output(AdminNoContentResponseSchema),
  }),

  // Spam Top-up
  spamTopup: oc.router({
    list: oc
      .route({ method: "GET", path: "/admin/v2/spam-topup-list" })
      .input(AdminListQuerySchema)
      .output(AdminSpamTopupListResponseSchema),
    delete: oc
      .route({ method: "POST", path: "/admin/v2/topup-spam/delete" })
      .input(AdminV2SpamTopupDeleteInputSchema)
      .output(AdminSpamTopupDeleteResponseSchema),
  }),
});

// ─── System ────────────────────────────────────────────────────────

const system = oc.router({
  status: oc
    .route({ method: "GET", path: "/system/status" })
    .input(AdminListQuerySchema.optional())
    .output(AdminSystemStatusListResponseSchema),
  updateStatus: oc
    .route({ method: "POST", path: "/system/update-status" })
    .input(SystemUpdateStatusInputSchema)
    .output(AdminSystemStatusSchema),
});

// ─── Wallet V1 ─────────────────────────────────────────────────────

const wallet = oc.router({
  // Auth
  register: oc
    .route({ method: "POST", path: "/wallet/v1/register" })
    .input(WalletRegisterInputSchema)
    .output(GenericResponseSchema),
  auth: oc
    .route({ method: "POST", path: "/wallet/v1/auth" })
    .input(WalletAuthInputSchema)
    .output(GenericResponseSchema),

  // Balance
  balance: oc
    .route({ method: "GET", path: "/wallet/v1/balance" })
    .output(GenericResponseSchema),

  // Supported currencies
  supportedCurrencies: oc
    .route({ method: "GET", path: "/wallet/v1/supported-currencies" })
    .output(GenericResponseSchema),

  // Exchange rate
  exchangeRate: oc
    .route({ method: "GET", path: "/wallet/v1/exchange-rate" })
    .input(WalletExchangeRateInputSchema)
    .output(WalletExchangeRateResponseSchema),

  // Deposit address
  requestDepositAddress: oc
    .route({ method: "POST", path: "/wallet/v1/request-deposit-address" })
    .input(WalletRequestDepositAddressInputSchema)
    .output(WalletRequestDepositAddressResponseSchema),

  // Transactions
  transactions: oc.router({
    list: oc
      .route({ method: "GET", path: "/wallet/v1/transactions" })
      .input(WalletTransactionListInputSchema)
      .output(GenericResponseSchema),
    detail: oc
      .route({ method: "GET", path: "/wallet/v1/transactions/{uuid}" })
      .input(WalletTransactionDetailInputSchema)
      .output(GenericResponseSchema),
  }),

  // Ongoing transaction
  ongoingTransaction: oc
    .route({ method: "GET", path: "/wallet/v1/ongoing-transaction" })
    .output(GenericResponseSchema),

  // Limits
  myLimit: oc
    .route({ method: "GET", path: "/wallet/v1/my-limit" })
    .output(GenericResponseSchema),

  // Banking
  banking: oc.router({
    list: oc
      .route({ method: "GET", path: "/wallet/v1/bankings" })
      .output(BankAccountListResponseSchema),
    listPayment: oc
      .route({ method: "GET", path: "/wallet/v1/bankings/payment" })
      .output(BankAccountListResponseSchema),
    add: oc
      .route({ method: "POST", path: "/wallet/v1/banking" })
      .input(WalletBankingAddInputSchema)
      .output(GenericResponseSchema),
    edit: oc
      .route({ method: "PUT", path: "/wallet/v1/banking/{bank_uuid}" })
      .input(WalletBankingEditInputSchema)
      .output(GenericResponseSchema),
    delete: oc
      .route({ method: "DELETE", path: "/wallet/v1/banking/{bank_uuid}" })
      .input(WalletBankingUuidInputSchema)
      .output(GenericResponseSchema),
  }),

  // Address book
  address: oc.router({
    list: oc
      .route({ method: "GET", path: "/wallet/v1/address" })
      .output(GenericResponseSchema),
    add: oc
      .route({ method: "POST", path: "/wallet/v1/address" })
      .input(WalletP2PAddressInputSchema)
      .output(GenericResponseSchema),
    edit: oc
      .route({ method: "PUT", path: "/wallet/v1/address/{address_uuid}" })
      .input(WalletP2PAddressEditInputSchema)
      .output(GenericResponseSchema),
    delete: oc
      .route({ method: "DELETE", path: "/wallet/v1/address/{address_uuid}" })
      .input(WalletP2PAddressUuidInputSchema)
      .output(GenericResponseSchema),
  }),

  // Merchant
  merchant: oc.router({
    get: oc
      .route({ method: "GET", path: "/wallet/v1/merchant" })
      .output(WalletMerchantResponseSchema),
    create: oc
      .route({ method: "POST", path: "/wallet/v1/merchant" })
      .input(WalletMerchantAddInputSchema)
      .output(WalletMerchantResponseSchema),
    update: oc
      .route({ method: "PUT", path: "/wallet/v1/merchant" })
      .input(WalletMerchantEditInputSchema)
      .output(WalletMerchantResponseSchema),
    uploadImage: oc
      .route({ method: "POST", path: "/wallet/v1/merchant/image" })
      .output(WalletMerchantUploadImageResponseSchema),
  }),

  // Escrow
  escrow: oc.router({
    submitDeposit: oc
      .route({ method: "POST", path: "/wallet/v1/escrow/submit-deposit" })
      .input(WalletEscrowSubmitDepositInputSchema)
      .output(GenericResponseSchema),
  }),

  // Payment links
  paymentLink: oc.router({
    list: oc
      .route({ method: "GET", path: "/wallet/v1/payment-links" })
      .input(WalletTransactionListInputSchema)
      .output(GenericResponseSchema),
    detail: oc
      .route({ method: "GET", path: "/wallet/v1/payment-link/{payment_uuid}" })
      .input(WalletPaymentLinkDetailInputSchema)
      .output(GenericResponseSchema),
    create: oc
      .route({ method: "POST", path: "/wallet/v1/payment-link" })
      .input(WalletPaymentLinkCreateInputSchema)
      .output(GenericResponseSchema),
    void: oc
      .route({ method: "POST", path: "/wallet/v1/payment-link/void" })
      .input(WalletPaymentLinkVoidInputSchema)
      .output(GenericResponseSchema),
  }),

  // PPOB
  ppob: oc.router({
    services: oc
      .route({ method: "GET", path: "/wallet/v1/ppob/services" })
      .output(GenericResponseSchema),
    products: oc
      .route({ method: "GET", path: "/wallet/v1/ppob/products" })
      .input(WalletPpobProductListInputSchema)
      .output(GenericResponseSchema),
    recentDestinations: oc
      .route({ method: "GET", path: "/wallet/v1/ppob/recent-destinations" })
      .input(WalletPpobRecentDestinationsInputSchema)
      .output(WalletPpobRecentDestinationsResponseSchema),
    mobileProviderValidate: oc
      .route({
        method: "GET",
        path: "/wallet/v1/ppob/mobile-provider/validate",
      })
      .input(WalletPpobMobileProviderInputSchema)
      .output(GenericResponseSchema),
    plnInquiry: oc
      .route({ method: "POST", path: "/wallet/v1/ppob/pln/inquiry" })
      .input(WalletPpobPlnInquiryInputSchema)
      .output(WalletPpobPlnInquiryResponseSchema),
    prepaidOrders: oc
      .route({ method: "POST", path: "/wallet/v1/ppob/prepaid/orders" })
      .input(WalletPpobPrepaidOrderInputSchema)
      .output(WalletPpobOrderEscrowResponseSchema),
    postpaidInquiry: oc
      .route({ method: "POST", path: "/wallet/v1/ppob/postpaid/inquiry" })
      .input(WalletPpobPostpaidInquiryInputSchema)
      .output(GenericResponseSchema),
    postpaidOrders: oc
      .route({ method: "POST", path: "/wallet/v1/ppob/postpaid/orders" })
      .input(WalletPpobPostpaidOrderInputSchema)
      .output(WalletPpobOrderEscrowResponseSchema),
  }),

  // User restrictions
  user: oc.router({
    restrictions: oc
      .route({ method: "GET", path: "/wallet/v1/user/restrictions" })
      .output(GenericResponseSchema),
  }),

  // Migrations
  migrations: oc.router({
    status: oc
      .route({ method: "GET", path: "/wallet/migrations/status" })
      .output(WalletMigrationStatusResponseSchema),
    createUnsignedJs: oc
      .route({ method: "POST", path: "/wallet/migrations/create-unsigned-js" })
      .output(WalletMigrationCreateUnsignedJsResponseSchema),
    returnJsMintSc: oc
      .route({ method: "POST", path: "/wallet/migrations/return-js-mint-sc" })
      .output(WalletMigrationReturnJsMintScResponseSchema),
  }),
});

// ─── Wallet V2 ─────────────────────────────────────────────────────

const walletV2 = oc.router({
  // Activities
  activities: oc.router({
    list: oc
      .route({ method: "GET", path: "/v2/wallet/activities" })
      .input(WalletV2ActivityListInputSchema)
      .output(WalletV2ActivityListResponseSchema),
    detail: oc
      .route({ method: "GET", path: "/v2/wallet/activity/{activity_uuid}" })
      .input(WalletV2ActivityDetailInputSchema)
      .output(GenericResponseSchema),
  }),

  // Redeem
  redeem: oc.router({
    initiate: oc
      .route({ method: "POST", path: "/v2/wallet/redeem/initiate" })
      .input(WalletV2RedeemInitiateInputSchema)
      .output(WalletV2RedeemInitiateResponseSchema),
    toBank: oc
      .route({ method: "POST", path: "/v2/wallet/redeem-to-bank/send" })
      .input(WalletV2RedeemBankSendInputSchema)
      .output(WalletV2RedeemSendResponseSchema),
    toEwallet: oc
      .route({ method: "POST", path: "/v2/wallet/redeem-to-ewallet/send" })
      .input(WalletV2RedeemEwalletSendInputSchema)
      .output(WalletV2RedeemSendResponseSchema),
  }),

  // Pay
  pay: oc.router({
    bankTransferFee: oc
      .route({ method: "GET", path: "/v2/wallet/pay/bank_transfer/fee" })
      .output(WalletV2BankTransferFeeResponseSchema),
    bankTransferInitiate: oc
      .route({ method: "POST", path: "/v2/wallet/pay/bank_transfer/initiate" })
      .input(WalletV2PayBankTransferInitiateInputSchema)
      .output(WalletV2PayBankTransferInitiateResponseSchema),
    bankTransferSend: oc
      .route({ method: "POST", path: "/v2/wallet/pay/bank_transfer/send" })
      .input(WalletV2PayBankTransferSendInputSchema)
      .output(WalletV2PayBankTransferSendResponseSchema),
  }),

  // Transfer
  transfer: oc.router({
    initiate: oc
      .route({ method: "POST", path: "/v2/wallet/transfer/initiate" })
      .input(WalletV2TransferInitiateInputSchema)
      .output(WalletV2TransferInitiateResponseSchema),
    send: oc
      .route({ method: "POST", path: "/v2/wallet/transfer/send" })
      .input(WalletV2TransferSendInputSchema)
      .output(WalletV2TransferSendResponseSchema),
  }),

  // Banking
  banking: oc.router({
    supportedBanks: oc
      .route({ method: "GET", path: "/v2/wallet/supported-banks" })
      .output(SupportedBankListResponseSchema),
    add: oc
      .route({ method: "POST", path: "/v2/wallet/banking" })
      .input(WalletV2BankingAddInputSchema)
      .output(
        z.union([BankAccountResponseSchema, LegacyInvalidStatusResponseSchema])
      ),
    update: oc
      .route({ method: "POST", path: "/v2/wallet/banking/{bank_uuid}/update" })
      .input(WalletV2BankingEditInputSchema)
      .output(
        z.union([BankAccountResponseSchema, LegacyInvalidStatusResponseSchema])
      ),
    validation: oc
      .route({
        method: "GET",
        path: "/v2/wallet/banking/{bank_uuid}/validation",
      })
      .input(WalletV2BankingUuidInputSchema)
      .output(BankAccountResponseSchema),
    favorite: oc
      .route({
        method: "PATCH",
        path: "/v2/wallet/banking/{bank_uuid}/favorite",
      })
      .input(WalletV2BankingFavoriteInputSchema)
      .output(BankAccountResponseSchema),
  }),

  // E-wallet
  ewallet: oc.router({
    supportedEwallets: oc
      .route({ method: "GET", path: "/v2/wallet/supported-ewallet" })
      .output(SupportedEwalletListResponseSchema),
    list: oc
      .route({ method: "GET", path: "/v2/wallet/ewallet-accounts" })
      .output(EwalletAccountListResponseSchema),
    add: oc
      .route({ method: "POST", path: "/v2/wallet/ewallet-account" })
      .input(WalletV2EwalletAddInputSchema)
      .output(
        z.union([
          EwalletAccountResponseSchema,
          LegacyInvalidStatusResponseSchema,
        ])
      ),
    update: oc
      .route({
        method: "POST",
        path: "/v2/wallet/ewallet-account/{uuid}/update",
      })
      .input(WalletV2EwalletEditInputSchema)
      .output(
        z.union([
          EwalletAccountResponseSchema,
          LegacyInvalidStatusResponseSchema,
        ])
      ),
    delete: oc
      .route({ method: "DELETE", path: "/v2/wallet/ewallet-account/{uuid}" })
      .input(WalletV2EwalletUuidInputSchema)
      .output(NoContentResponseSchema),
    validation: oc
      .route({
        method: "GET",
        path: "/v2/wallet/ewallet-account/{uuid}/validation",
      })
      .input(WalletV2EwalletUuidInputSchema)
      .output(EwalletAccountResponseSchema),
    favorite: oc
      .route({
        method: "PATCH",
        path: "/v2/wallet/ewallet-account/{uuid}/favorite",
      })
      .input(WalletV2EwalletFavoriteInputSchema)
      .output(EwalletAccountResponseSchema),
  }),

  // Referral
  referral: oc.router({
    submit: oc
      .route({ method: "POST", path: "/v2/wallet/referral" })
      .input(WalletV2ReferralInputSchema)
      .output(WalletV2ReferralSubmitResponseSchema),
    journey: oc
      .route({ method: "GET", path: "/v2/wallet/referral/journey" })
      .output(WalletV2ReferralJourneyResponseSchema),
    rewards: oc
      .route({ method: "GET", path: "/v2/wallet/referral" })
      .output(WalletV2ReferralDetailsResponseSchema),
  }),

  // Virtual account
  virtualAccount: oc.router({
    supported: oc
      .route({ method: "GET", path: "/v2/wallet/supported-virtual-account" })
      .output(WalletV2VirtualAccountSupportedResponseSchema),
    topUpInstruction: oc
      .route({ method: "GET", path: "/v2/wallet/top-up-bank-instruction" })
      .output(WalletV2VirtualAccountTopUpInstructionResponseSchema),
    create: oc
      .route({ method: "POST", path: "/v2/wallet/virtual-account" })
      .input(WalletV2VirtualAccountInputSchema)
      .output(WalletV2VirtualAccountCreateResponseSchema),
  }),

  // KYC
  kyc: oc.router({
    myDetail: oc
      .route({ method: "POST", path: "/v2/wallet/kyc/my-detail" })
      .output(WalletV2KycDetailResponseSchema),
    benefits: oc
      .route({ method: "GET", path: "/v2/wallet/kyc/benefits" })
      .output(WalletV2KycBenefitsResponseSchema),
    getAccessToken: oc
      .route({ method: "GET", path: "/v2/wallet/kyc/get-access-token" })
      .input(WalletV2KycAccessTokenInputSchema)
      .output(WalletV2KycAccessTokenResponseSchema),
  }),
});

// ─── Root Contract ─────────────────────────────────────────────────

export const contract = oc.router({
  admin,
  adminV2,
  system,
  wallet,
  walletV2,
});
